import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@roger/ui-components';
import { Clipboard } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FormTreeQuestion } from './formSchemaTypes';
import { validateLayoutJsonForSection } from './formSchemaUtils';

interface SectionLayoutJsonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sectionDisplayName: string;
  value: string;
  questions: FormTreeQuestion[];
  /** Copy the current dialog draft (not the parent editor value). */
  onCopyLayoutJson: (text: string) => void;
  onCopyLlmBundle: () => void;
  onApply: (next: string) => void;
}

export function SectionLayoutJsonDialog({
  open,
  onOpenChange,
  sectionDisplayName,
  value,
  onApply,
  questions,
  onCopyLayoutJson,
  onCopyLlmBundle,
}: SectionLayoutJsonDialogProps) {
  const [draftText, setDraftText] = useState(value);

  // Re-sync when the dialog opens or the parent editor switches layout version.
  useEffect(() => {
    if (open) {
      setDraftText(value);
    }
  }, [open, value]);

  const validation = useMemo(
    () => validateLayoutJsonForSection(draftText, questions),
    [draftText, questions]
  );
  const canApply = validation.ok;

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleApply = () => {
    if (!canApply) return;
    onApply(draftText);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) handleCancel();
        else onOpenChange(true);
      }}
    >
      <DialogContent className='flex max-h-[min(90vh,52rem)] max-w-2xl flex-col'>
        <DialogHeader className='shrink-0'>
          <DialogTitle>Layout JSON — {sectionDisplayName}</DialogTitle>
          <DialogDescription>
            Edit the Roger Layout Schema for this section. Changes apply to the
            editor when you click OK; use Save in the editor to persist.
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-wrap justify-end gap-1.5'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => onCopyLayoutJson(draftText)}
            disabled={!draftText.trim()}
          >
            <Clipboard className='mr-1 h-3.5 w-3.5' />
            Copy
          </Button>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={onCopyLlmBundle}
            disabled={questions.length === 0}
          >
            <Clipboard className='mr-1 h-3.5 w-3.5' />
            Copy with instructions
          </Button>
        </div>
        <Textarea
          value={draftText}
          onChange={e => setDraftText(e.target.value)}
          placeholder='Roger Layout Schema JSON here...'
          className='min-h-[22rem] flex-1 font-mono text-[11px] leading-snug'
          spellCheck={false}
        />
        {!validation.ok ? (
          <p className='text-xs text-destructive'>{validation.error}</p>
        ) : null}
        <DialogFooter className='shrink-0'>
          <Button type='button' variant='outline' onClick={handleCancel}>
            Cancel
          </Button>
          <Button type='button' onClick={handleApply} disabled={!canApply}>
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
